import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { 
  FileText, Plus, Calendar, Clock, Users, CheckCircle, AlertTriangle,
  BookOpen, Send, Eye, Award, Search, Upload, FileUp, X, Paperclip, AlertCircle
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ASSIGNMENT_TYPES = [
  { value: 'all', label: 'All Types', icon: FileText },
  { value: 'homework', label: 'Homework', icon: BookOpen },
  { value: 'assignment', label: 'Assignment', icon: FileText },
  { value: 'project', label: 'Project', icon: Users },
  { value: 'classwork', label: 'Class Work', icon: CheckCircle }
];

const HomeworkPage = () => {
  const { getAuthHeader, user } = useAuth();
  const [activeTab, setActiveTab] = useState('assignments');
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState([]);
  const [filteredAssignments, setFilteredAssignments] = useState([]);
  const [classes, setClasses] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [typeFilter, setTypeFilter] = useState('all');
  
  // Dialogs
  const [createDialog, setCreateDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [submitDialog, setSubmitDialog] = useState(false);
  const [gradeDialog, setGradeDialog] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [submissionsDialog, setSubmissionsDialog] = useState(false);
  const [assignmentSubmissions, setAssignmentSubmissions] = useState(null);
  
  // Forms
  const [assignmentForm, setAssignmentForm] = useState({
    title: '', description: '', class_id: '', subject: '', due_date: '', total_marks: 100, assignment_type: 'homework'
  });
  const [submitForm, setSubmitForm] = useState({ answer_text: '', files: [] });
  const [gradeForm, setGradeForm] = useState({ marks_obtained: 0, feedback: '' });
  const [uploading, setUploading] = useState(false);

  const isTeacher = user?.role === 'teacher' || user?.role === 'school_admin';

  useEffect(() => {
    fetchData();
  }, []);

  // Filter assignments by type
  useEffect(() => {
    if (typeFilter === 'all') {
      setFilteredAssignments(assignments);
    } else {
      setFilteredAssignments(assignments.filter(a => a.assignment_type === typeFilter));
    }
  }, [typeFilter, assignments]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [assignRes, classRes] = await Promise.all([
        axios.get(`${API_URL}/api/homework`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/classes`, { headers: getAuthHeader() })
      ]);
      setAssignments(assignRes.data);
      setClasses(classRes.data);
      
      if (isTeacher) {
        const analyticsRes = await axios.get(`${API_URL}/api/homework/analytics/summary`, { headers: getAuthHeader() });
        setAnalytics(analyticsRes.data);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const createAssignment = async () => {
    if (!assignmentForm.title || !assignmentForm.class_id || !assignmentForm.subject || !assignmentForm.due_date) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/homework`, null, {
        headers: getAuthHeader(),
        params: assignmentForm
      });
      toast.success('Assignment created');
      setCreateDialog(false);
      setAssignmentForm({ title: '', description: '', class_id: '', subject: '', due_date: '', total_marks: 100, assignment_type: 'homework' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create assignment');
    }
  };

  const viewAssignment = async (assignment) => {
    setSelectedAssignment(assignment);
    if (isTeacher) {
      try {
        const res = await axios.get(`${API_URL}/api/homework/${assignment.id}/submissions`, { headers: getAuthHeader() });
        setSubmissions(res.data);
      } catch (error) {
        setSubmissions([]);
      }
    }
    setViewDialog(true);
  };

  const submitAssignment = async () => {
    if (!selectedAssignment) return;
    
    setUploading(true);
    try {
      // If files are selected, use file upload endpoint
      if (submitForm.files && submitForm.files.length > 0) {
        const formData = new FormData();
        if (submitForm.answer_text) {
          formData.append('answer_text', submitForm.answer_text);
        }
        
        // Validate file count
        if (submitForm.files.length > 5) {
          toast.error('Maximum 5 files allowed per submission');
          setUploading(false);
          return;
        }
        
        // Validate file sizes (300 KB limit)
        for (const file of submitForm.files) {
          if (file.size > 300 * 1024) {
            toast.error(`File "${file.name}" exceeds 300 KB limit`);
            setUploading(false);
            return;
          }
          formData.append('files', file);
        }
        
        const res = await axios.post(
          `${API_URL}/api/homework/${selectedAssignment.id}/submit-with-files`,
          formData,
          {
            headers: {
              ...getAuthHeader(),
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        toast.success(res.data.is_late ? `Submitted (Late) - ${res.data.files_uploaded} files` : `Submitted - ${res.data.files_uploaded} files uploaded`);
      } else {
        // Text only submission
        const res = await axios.post(`${API_URL}/api/homework/${selectedAssignment.id}/submit`, null, {
          headers: getAuthHeader(),
          params: { answer_text: submitForm.answer_text }
        });
        toast.success(res.data.is_late ? 'Submitted (Late)' : 'Assignment submitted');
      }
      
      setSubmitDialog(false);
      setSubmitForm({ answer_text: '', files: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 5) {
      toast.error('Maximum 5 files allowed');
      return;
    }
    setSubmitForm(prev => ({ ...prev, files }));
  };

  const removeFile = (index) => {
    setSubmitForm(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  const viewSubmissions = async (assignment) => {
    setSelectedAssignment(assignment);
    try {
      const res = await axios.get(`${API_URL}/api/homework/${assignment.id}/submissions`, {
        headers: getAuthHeader()
      });
      setAssignmentSubmissions(res.data);
      setSubmissionsDialog(true);
    } catch (error) {
      toast.error('Failed to load submissions');
    }
  };

  const openGradeDialog = (submission) => {
    setSelectedSubmission(submission);
    setGradeForm({ marks_obtained: submission.marks_obtained || 0, feedback: submission.feedback || '' });
    setGradeDialog(true);
  };

  const gradeSubmission = async () => {
    if (!selectedAssignment || !selectedSubmission) return;
    try {
      await axios.post(`${API_URL}/api/homework/${selectedAssignment.id}/grade/${selectedSubmission.id}`, null, {
        headers: getAuthHeader(),
        params: gradeForm
      });
      toast.success('Submission graded');
      setGradeDialog(false);
      viewAssignment(selectedAssignment);
    } catch (error) {
      toast.error('Failed to grade submission');
    }
  };

  const getStatusBadge = (assignment) => {
    const dueDate = new Date(assignment.due_date);
    const today = new Date();
    
    if (assignment.my_submission?.status === 'graded') {
      return <Badge className="bg-green-500">Graded</Badge>;
    }
    if (assignment.my_submission) {
      return <Badge variant="secondary">{assignment.my_submission.is_late ? 'Late Submitted' : 'Submitted'}</Badge>;
    }
    if (today > dueDate) {
      return <Badge variant="destructive">Overdue</Badge>;
    }
    return <Badge variant="outline">Pending</Badge>;
  };

  const getTypeBadge = (type) => {
    const colors = {
      homework: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
      assignment: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
      project: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      classwork: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300'
    };
    const labels = { homework: 'Homework', assignment: 'Assignment', project: 'Project', classwork: 'Class Work' };
    return <Badge className={colors[type] || colors.homework}>{labels[type] || 'Homework'}</Badge>;
  };

  const stats = [
    { title: 'Total Assignments', value: analytics.total_assignments || 0, icon: FileText, color: 'bg-blue-500' },
    { title: 'Submissions', value: analytics.total_submissions || 0, icon: Send, color: 'bg-green-500' },
    { title: 'Graded', value: analytics.graded || 0, icon: CheckCircle, color: 'bg-purple-500' },
    { title: 'Pending Review', value: analytics.pending_review || 0, icon: Clock, color: 'bg-amber-500' },
  ];

  return (
    <DashboardLayout title="Homework & Assignments">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="flex justify-between items-center">
          <TabsList>
            <TabsTrigger value="assignments">Assignments</TabsTrigger>
            {isTeacher && <TabsTrigger value="analytics">Analytics</TabsTrigger>}
          </TabsList>
          
          {isTeacher && (
            <Button onClick={() => setCreateDialog(true)} data-testid="create-assignment-btn">
              <Plus className="h-4 w-4 mr-2" /> Create Assignment
            </Button>
          )}
        </div>

        {/* Assignments Tab */}
        <TabsContent value="assignments" className="space-y-4">
          {/* Type Filter */}
          <div className="flex flex-wrap gap-2 pb-2">
            {ASSIGNMENT_TYPES.map(type => {
              const Icon = type.icon;
              const isActive = typeFilter === type.value;
              return (
                <Button
                  key={type.value}
                  variant={isActive ? "default" : "outline"}
                  size="sm"
                  onClick={() => setTypeFilter(type.value)}
                  data-testid={`filter-${type.value}`}
                  className="gap-1"
                >
                  <Icon className="h-3.5 w-3.5" />
                  {type.label}
                  {type.value !== 'all' && (
                    <span className="ml-1 text-xs opacity-70">
                      ({assignments.filter(a => a.assignment_type === type.value).length})
                    </span>
                  )}
                </Button>
              );
            })}
          </div>

          {isTeacher && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {stats.map((stat, i) => {
                const Icon = stat.icon;
                return (
                  <motion.div key={stat.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between pb-2">
                        <CardTitle className="text-xs font-medium text-muted-foreground">{stat.title}</CardTitle>
                        <div className={`${stat.color} p-2 rounded-lg`}><Icon className="h-3.5 w-3.5 text-white" /></div>
                      </CardHeader>
                      <CardContent><div className="text-2xl font-bold">{stat.value}</div></CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredAssignments.map((assignment, index) => (
              <motion.div key={assignment.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}>
                <Card className="hover:shadow-md transition-shadow" data-testid={`assignment-${assignment.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          {getTypeBadge(assignment.assignment_type)}
                          {!isTeacher && getStatusBadge(assignment)}
                        </div>
                        <CardTitle className="text-base">{assignment.title}</CardTitle>
                        <CardDescription>{assignment.subject} • {assignment.class_name}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-muted-foreground line-clamp-2">{assignment.description}</p>
                    
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        Due: {assignment.due_date}
                      </div>
                      <div className="flex items-center gap-1">
                        <Award className="h-4 w-4 text-amber-500" />
                        {assignment.total_marks} marks
                      </div>
                    </div>
                    
                    {isTeacher && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Users className="h-4 w-4" />
                        {assignment.submission_count || 0} submissions
                      </div>
                    )}
                    
                    {/* Student: Show grade if graded */}
                    {!isTeacher && assignment.my_submission?.status === 'graded' && (
                      <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded">
                        <p className="text-sm font-medium text-green-700 dark:text-green-400">
                          Score: {assignment.my_submission.marks_obtained}/{assignment.total_marks}
                        </p>
                        {assignment.my_submission.feedback && (
                          <p className="text-xs text-muted-foreground mt-1">{assignment.my_submission.feedback}</p>
                        )}
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="flex-1" onClick={() => viewAssignment(assignment)}>
                        <Eye className="h-4 w-4 mr-1" /> View
                      </Button>
                      {isTeacher && (
                        <Button variant="outline" size="sm" className="flex-1" onClick={() => viewSubmissions(assignment)}>
                          <Users className="h-4 w-4 mr-1" /> Submissions
                        </Button>
                      )}
                      {!isTeacher && !assignment.my_submission && (
                        <Button size="sm" className="flex-1" onClick={() => { setSelectedAssignment(assignment); setSubmitDialog(true); }}>
                          <Send className="h-4 w-4 mr-1" /> Submit
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
          
          {filteredAssignments.length === 0 && (
            <Card className="p-8 text-center">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {typeFilter === 'all' ? 'No assignments yet' : `No ${typeFilter} found`}
              </p>
            </Card>
          )}
        </TabsContent>

        {/* Analytics Tab (Teacher only) */}
        {isTeacher && (
          <TabsContent value="analytics" className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <CardHeader><CardTitle>Submission Overview</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span>Total Submissions</span>
                      <span className="font-bold">{analytics.total_submissions}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Graded</span>
                      <span className="font-bold text-green-600">{analytics.graded}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Pending Review</span>
                      <span className="font-bold text-amber-600">{analytics.pending_review}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Submission Rate</span>
                      <span className="font-bold">{analytics.submission_rate}%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Create Assignment Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Create Assignment</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title *</Label><Input value={assignmentForm.title} onChange={e => setAssignmentForm({...assignmentForm, title: e.target.value})} placeholder="Assignment title" /></div>
            <div><Label>Description</Label><Textarea value={assignmentForm.description} onChange={e => setAssignmentForm({...assignmentForm, description: e.target.value})} rows={3} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Type *</Label>
                <Select value={assignmentForm.assignment_type} onValueChange={v => setAssignmentForm({...assignmentForm, assignment_type: v})}>
                  <SelectTrigger data-testid="assignment-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="homework">Homework</SelectItem>
                    <SelectItem value="assignment">Assignment</SelectItem>
                    <SelectItem value="project">Project</SelectItem>
                    <SelectItem value="classwork">Class Work</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Class *</Label>
                <Select value={assignmentForm.class_id} onValueChange={v => setAssignmentForm({...assignmentForm, class_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Select class" /></SelectTrigger>
                  <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Subject *</Label><Input value={assignmentForm.subject} onChange={e => setAssignmentForm({...assignmentForm, subject: e.target.value})} placeholder="e.g., Mathematics" /></div>
              <div><Label>Due Date *</Label><Input type="date" value={assignmentForm.due_date} onChange={e => setAssignmentForm({...assignmentForm, due_date: e.target.value})} /></div>
            </div>
            <div><Label>Total Marks</Label><Input type="number" value={assignmentForm.total_marks} onChange={e => setAssignmentForm({...assignmentForm, total_marks: parseInt(e.target.value)})} /></div>
          </div>
          <DialogFooter><Button onClick={createAssignment} data-testid="submit-create-assignment">Create Assignment</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Assignment Dialog */}
      <Dialog open={viewDialog} onOpenChange={setViewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{selectedAssignment?.title}</DialogTitle></DialogHeader>
          {selectedAssignment && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                {getTypeBadge(selectedAssignment.assignment_type)}
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-muted-foreground">Subject:</span> {selectedAssignment.subject}</div>
                <div><span className="text-muted-foreground">Class:</span> {selectedAssignment.class_name}</div>
                <div><span className="text-muted-foreground">Due Date:</span> {selectedAssignment.due_date}</div>
                <div><span className="text-muted-foreground">Total Marks:</span> {selectedAssignment.total_marks}</div>
              </div>
              <div>
                <Label className="text-muted-foreground">Description</Label>
                <p className="mt-1">{selectedAssignment.description || 'No description'}</p>
              </div>
              
              {isTeacher && submissions.length > 0 && (
                <div>
                  <Label className="text-muted-foreground">Submissions ({submissions.length})</Label>
                  <div className="mt-2 space-y-2 max-h-60 overflow-auto">
                    {submissions.map(sub => (
                      <div key={sub.id} className="flex items-center justify-between p-3 border rounded">
                        <div>
                          <p className="font-medium">{sub.student_name}</p>
                          <p className="text-xs text-muted-foreground">{sub.student_roll} • {sub.is_late && <span className="text-red-500">Late</span>}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {sub.status === 'graded' ? (
                            <Badge className="bg-green-500">{sub.marks_obtained}/{selectedAssignment.total_marks}</Badge>
                          ) : (
                            <Button size="sm" onClick={() => openGradeDialog(sub)}>Grade</Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Submit Assignment Dialog (Student) */}
      <Dialog open={submitDialog} onOpenChange={setSubmitDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Submit Assignment</DialogTitle></DialogHeader>
          {selectedAssignment && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">{selectedAssignment.title}</p>
                <p className="text-sm text-muted-foreground">{selectedAssignment.subject} • {selectedAssignment.class_name}</p>
                <div className="flex items-center gap-2 mt-2 text-sm">
                  <Calendar className="h-4 w-4 text-amber-500" />
                  <span>Due: {selectedAssignment.due_date}</span>
                  {new Date() > new Date(selectedAssignment.due_date) && (
                    <Badge variant="destructive" className="ml-2">Overdue</Badge>
                  )}
                </div>
              </div>
              {selectedAssignment.description && (
                <div className="p-3 border rounded-lg">
                  <Label className="text-muted-foreground text-xs">Assignment Description</Label>
                  <p className="text-sm mt-1">{selectedAssignment.description}</p>
                </div>
              )}
              <div>
                <Label>Your Answer</Label>
                <Textarea 
                  value={submitForm.answer_text} 
                  onChange={e => setSubmitForm({...submitForm, answer_text: e.target.value})} 
                  rows={4} 
                  placeholder="Write your answer here (optional if uploading files)..." 
                  className="mt-1"
                />
              </div>
              
              {/* File Upload Section */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  Upload Files (Max 5 files, 300 KB each)
                </Label>
                <div className="border-2 border-dashed rounded-lg p-4 text-center">
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.xls,.xlsx"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="file-upload"
                    data-testid="file-upload-input"
                  />
                  <label 
                    htmlFor="file-upload" 
                    className="cursor-pointer flex flex-col items-center gap-2"
                  >
                    <FileUp className="h-8 w-8 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      Click to upload or drag and drop
                    </span>
                    <span className="text-xs text-muted-foreground">
                      PDF, DOC, TXT, Images, Excel (Max 300 KB each)
                    </span>
                  </label>
                </div>
                
                {/* Selected Files List */}
                {submitForm.files && submitForm.files.length > 0 && (
                  <div className="space-y-2 mt-2">
                    <Label className="text-sm text-muted-foreground">
                      Selected Files ({submitForm.files.length}/5)
                    </Label>
                    {submitForm.files.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-blue-500" />
                          <span className="text-sm truncate max-w-[200px]">{file.name}</span>
                          <span className="text-xs text-muted-foreground">
                            ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                          {file.size > 300 * 1024 && (
                            <Badge variant="destructive" className="text-xs">Too large</Badge>
                          )}
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => removeFile(index)}
                          className="h-6 w-6 p-0"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubmitDialog(false)}>Cancel</Button>
            <Button 
              onClick={submitAssignment} 
              disabled={uploading || (!submitForm.answer_text && (!submitForm.files || submitForm.files.length === 0))}
              data-testid="confirm-submit-assignment"
            >
              {uploading ? (
                <>Uploading...</>
              ) : (
                <><Send className="h-4 w-4 mr-2" /> Submit</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Submissions Dialog (Teacher) */}
      <Dialog open={submissionsDialog} onOpenChange={setSubmissionsDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Submissions - {assignmentSubmissions?.assignment?.title}</DialogTitle>
            <p className="text-sm text-muted-foreground">
              {assignmentSubmissions?.submitted_count || 0} of {assignmentSubmissions?.total_students || 0} students submitted
            </p>
          </DialogHeader>
          
          {assignmentSubmissions && (
            <div className="space-y-4">
              {/* Submitted Students */}
              <div>
                <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Submitted ({assignmentSubmissions.submissions?.length || 0})
                </Label>
                <div className="space-y-3">
                  {assignmentSubmissions.submissions?.map(sub => (
                    <div key={sub.id} className="border rounded-lg p-3">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-medium">{sub.student_name}</p>
                          <p className="text-xs text-muted-foreground">
                            Roll: {sub.roll_number} • {sub.class_name}
                          </p>
                        </div>
                        <div className="text-right">
                          <Badge variant={sub.status === 'late' ? 'destructive' : sub.status === 'graded' ? 'default' : 'secondary'}>
                            {sub.status}
                          </Badge>
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(sub.submitted_at).toLocaleString('en-IN')}
                          </p>
                        </div>
                      </div>
                      
                      {/* Answer Text */}
                      {sub.answer_text && (
                        <div className="bg-muted/50 p-2 rounded text-sm mb-2">
                          <Label className="text-xs text-muted-foreground">Answer:</Label>
                          <p className="mt-1">{sub.answer_text}</p>
                        </div>
                      )}
                      
                      {/* Uploaded Files */}
                      {sub.uploaded_files && sub.uploaded_files.length > 0 && (
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground flex items-center gap-1">
                            <Paperclip className="h-3 w-3" />
                            Uploaded Files ({sub.uploaded_files.length})
                          </Label>
                          <div className="flex flex-wrap gap-2">
                            {sub.uploaded_files.map((file, idx) => (
                              <a
                                key={idx}
                                href={`${API_URL}${file.file_path}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded hover:bg-blue-100"
                              >
                                <FileText className="h-3 w-3" />
                                {file.original_name}
                                <span className="text-blue-500">({file.file_size_kb} KB)</span>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Grade Section */}
                      <div className="flex items-center justify-between mt-2 pt-2 border-t">
                        {sub.marks_obtained !== null ? (
                          <div className="text-sm">
                            <span className="font-medium">{sub.marks_obtained}</span>
                            <span className="text-muted-foreground">/{assignmentSubmissions.assignment?.total_marks} marks</span>
                          </div>
                        ) : (
                          <span className="text-sm text-amber-600">Not graded</span>
                        )}
                        <Button size="sm" variant="outline" onClick={() => openGradeDialog(sub)}>
                          {sub.marks_obtained !== null ? 'Edit Grade' : 'Grade'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Not Submitted Students */}
              {assignmentSubmissions.not_submitted?.length > 0 && (
                <div>
                  <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                    <AlertCircle className="h-4 w-4 text-amber-500" />
                    Not Submitted ({assignmentSubmissions.not_submitted.length})
                  </Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {assignmentSubmissions.not_submitted.map(st => (
                      <div key={st.student_id} className="text-sm p-2 bg-amber-50 rounded">
                        <p className="font-medium">{st.student_name}</p>
                        <p className="text-xs text-muted-foreground">Roll: {st.roll_number}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Grade Submission Dialog (Teacher) */}
      <Dialog open={gradeDialog} onOpenChange={setGradeDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Grade Submission</DialogTitle></DialogHeader>
          {selectedSubmission && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{selectedSubmission.student_name}</p>
                    <p className="text-xs text-muted-foreground">{selectedSubmission.student_roll}</p>
                  </div>
                  <div className="text-right">
                    <Badge variant={selectedSubmission.is_late ? 'destructive' : 'secondary'}>
                      {selectedSubmission.is_late ? 'Late Submission' : 'On Time'}
                    </Badge>
                    <p className="text-xs text-muted-foreground mt-1">
                      Submitted: {new Date(selectedSubmission.submitted_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-3 border rounded-lg">
                <Label className="text-muted-foreground text-xs">Student's Answer</Label>
                <p className="text-sm mt-1 whitespace-pre-wrap">{selectedSubmission.answer_text || 'No text answer provided'}</p>
              </div>
              <div>
                <Label>Marks (out of {selectedAssignment?.total_marks}) *</Label>
                <Input 
                  type="number" 
                  value={gradeForm.marks_obtained} 
                  onChange={e => setGradeForm({...gradeForm, marks_obtained: parseFloat(e.target.value) || 0})} 
                  max={selectedAssignment?.total_marks}
                  min={0}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Feedback (Optional)</Label>
                <Textarea 
                  value={gradeForm.feedback} 
                  onChange={e => setGradeForm({...gradeForm, feedback: e.target.value})} 
                  rows={3} 
                  placeholder="Provide feedback to the student..."
                  className="mt-1"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setGradeDialog(false)}>Cancel</Button>
            <Button onClick={gradeSubmission} data-testid="confirm-grade-submission">
              <CheckCircle className="h-4 w-4 mr-2" /> Save Grade
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default HomeworkPage;
